import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Badge, Alert, ButtonGroup, Nav, Modal, ProgressBar } from 'react-bootstrap';
import { 
    Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, 
    Line, ComposedChart, LabelList
} from 'recharts';
import { TrendingUp, TrendingDown, Filter, Users, Radio, Award, Trophy } from 'lucide-react';
import { toast } from 'react-toastify';
import apiClient from '../../api';
import { getClients, getVehicles, getDrivers, getBookingAnalytics, exportBookingSearchLogs, getAreas, getCostCenters, getUniqueVouchers, getBases, getProviders, getQualityInsights, updateSurveyManagement, syncBookingSurveys, updateIncidentManagement, getDispatcherDispersionInsights } from '../api';

import SearchableMultiSelect from './SearchableMultiSelect';
import SearchableSelect from './SearchableSelect';
import BookingSearchLogsView from './BookingSearchLogsView';
import BookingHeatmapView from './BookingHeatmapView';
import BookingProviderInsightsView from './BookingProviderInsightsView';
import BookingSentinelView from './BookingSentinelView';

const InsightsDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [viewMode, setViewMode] = useState('client'); // 'client' or 'holding'
    const [activeTab, setActiveTab] = useState('performance'); // 'performance' or 'booking'
    const [bookingSubTab, setBookingSubTab] = useState('overview'); // 'overview', 'logs', 'heatmap'
    const [bookingData, setBookingData] = useState(null);
    const [bookingLoading, setBookingLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('');
    
    // Calidad & Encuestas de Booking.com
    const [bookingQualityData, setBookingQualityData] = useState(null);
    const [bookingQualityLoading, setBookingQualityLoading] = useState(false);
    const [isSyncingSurveys, setIsSyncingSurveys] = useState(false);
    const [driverOptions, setDriverOptions] = useState([]);
    const [providerOptions, setProviderOptions] = useState([]);
    const [qualityFilters, setQualityFilters] = useState({
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        country_code: '',
        zone_name: '',
        driver_id: '',
        provider_id: '',
        status: ''
    });
    const [showManageModal, setShowManageModal] = useState(false);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [managementNotes, setManagementNotes] = useState('');
    const [managementStatus, setManagementStatus] = useState('REVIEWED');
    const [filterCriticalPendingOnly, setFilterCriticalPendingOnly] = useState(false);
    const [qualityTab, setQualityTab] = useState('surveys');
    const [updatingIncidentId, setUpdatingIncidentId] = useState(null);

    // Opciones para filtros
    const [clientOptions, setClientOptions] = useState([]);
    const [vehicleOptions, setVehicleOptions] = useState([]);
    const [areaOptions, setAreaOptions] = useState([]);
    const [costCenterOptions, setCostCenterOptions] = useState([]);
    const [voucherOptions, setVoucherOptions] = useState([]);
    const [segmentations] = useState(['Corporativos', 'Masivos', 'Eventos', 'Particulares']);
    const [bases, setBases] = useState([]);

    const zoneOptionsList = useMemo(() => {
        const list = bases.map(b => ({ id: b.name, name: b.name }));
        if (!list.some(z => z.id === 'Punta Cana')) {
            list.push({ id: 'Punta Cana', name: 'Punta Cana' });
        }
        return list;
    }, [bases]);
    
    // Estado de filtros
    const [filters, setFilters] = useState({
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        client_ids: [],
        segmentations: [],
        vehicle_ids: [],
        cost_center_ids: [],
        area_ids: [],
        vouchers: [],
        compare_to: 'previous_year',
        countries: [],
        cities: []
    });
    const [originalDates, setOriginalDates] = useState(null);

    // Cargar Bases del backend
    useEffect(() => {
        const fetchBases = async () => {
            try {
                const bs = await getBases();
                setBases(bs || []);
            } catch (err) {
                console.error("Error loading bases:", err);
            }
        };
        fetchBases();
    }, []);

    const countryOptions = useMemo(() => {
        const codes = [...new Set(bases.map(b => b.country_code).filter(Boolean).map(c => c.trim().toUpperCase()))];
        return codes.map(c => ({
            id: c,
            name: c === 'CL' ? 'Chile (CL)' : c === 'DO' ? 'República Dominicana (DO)' : c
        }));
    }, [bases]);

    // Filtrar Centros de Costo dinámicamente según los clientes seleccionados
    const filteredCostCenterOptions = useMemo(() => {
        if (!filters.client_ids || filters.client_ids.length === 0) {
            return costCenterOptions;
        }
        const selectedClientIds = filters.client_ids.map(Number);
        return costCenterOptions.filter(cc => selectedClientIds.includes(Number(cc.client_id)));
    }, [costCenterOptions, filters.client_ids]);

    // Filtrar Áreas dinámicamente según los clientes seleccionados
    const filteredAreaOptions = useMemo(() => {
        if (!filters.client_ids || filters.client_ids.length === 0) {
            return areaOptions;
        }
        const selectedClientIds = filters.client_ids.map(Number);
        return areaOptions.filter(a => !a.client_id || selectedClientIds.includes(Number(a.client_id)));
    }, [areaOptions, filters.client_ids]);

    const baseOptionsList = useMemo(() => {
        const filtered = filters.countries && filters.countries.length > 0
            ? bases.filter(b => b.country_code && filters.countries.includes(b.country_code.trim().toUpperCase()))
            : bases;
        return filtered.map(b => ({
            id: b.name,
            name: b.name
        }));
    }, [bases, filters.countries]);

    const handleCountryChange = (newCountries) => {
        setFilters(prev => {
            const allowedBases = bases.filter(b => b.country_code && newCountries.includes(b.country_code.trim().toUpperCase())).map(b => b.name);
            const newCities = prev.cities.filter(c => newCountries.length === 0 || allowedBases.includes(c));
            return {
                ...prev,
                countries: newCountries,
                cities: newCities
            };
        });
    };

    const [sortConfig, setSortConfig] = useState({ key: 'current_sales', direction: 'desc' });

    const handleSort = (key) => {
        let direction = 'desc';
        if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = 'asc';
        }
        setSortConfig({ key, direction });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <span className="text-muted ms-1" style={{ opacity: 0.3, fontSize: '11px' }}>↕</span>;
        return sortConfig.direction === 'asc' 
            ? <span className="text-primary ms-1" style={{ fontSize: '11px' }}>▲</span> 
            : <span className="text-primary ms-1" style={{ fontSize: '11px' }}>▼</span>;
    };

    const sortedTableData = useMemo(() => {
        const tableList = (viewMode === 'holding' && data?.holding_table) ? data.holding_table : (data?.client_table || []);
        if (!sortConfig.key) return tableList;
        return [...tableList].sort((a, b) => {
            let valA = a[sortConfig.key];
            let valB = b[sortConfig.key];
            
            if (typeof valA === 'string') {
                return sortConfig.direction === 'asc' 
                    ? valA.localeCompare(valB)
                    : valB.localeCompare(valA);
            }
            
            // Numbers
            valA = parseFloat(valA) || 0;
            valB = parseFloat(valB) || 0;
            return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        });
    }, [data, viewMode, sortConfig]);

    const loadOptions = useCallback(async () => {
        try {
            const [cData, vData, dData, aData, ccData, pData] = await Promise.all([
                getClients(),
                getVehicles(),
                getDrivers(),
                getAreas(),
                getCostCenters(),
                getProviders()
            ]);
            setClientOptions(cData);
            setAreaOptions(aData.map(a => ({
                id: a.id,
                name: a.name,
                client_id: a.client_id
            })));
            setDriverOptions(dData);
            setProviderOptions(pData);
            setCostCenterOptions(ccData.map(cc => ({
                id: cc.id,
                name: cc.code ? `${cc.code} - ${cc.name}` : cc.name,
                client_id: cc.client_id
            })));
            // Formatear vehículos para mostrar Código Interno y Conductor, omitiendo la patente
            setVehicleOptions(vData.map(v => {
                const driver = dData.find(d => d.id === v.current_driver_id);
                return {
                    id: v.id,
                    name: `Móvil ${v.internal_code || 'S/M'}${driver ? ` - ${driver.full_name}` : ' (Sin Conductor)'}`,
                    code: v.internal_code
                };
            }));
        } catch (err) {
            console.error("Error cargando opciones de filtro:", err);
        }
    }, []);

    const loadVouchers = useCallback(async () => {
        try {
            const vData = await getUniqueVouchers(filters.start_date, filters.end_date);
            setVoucherOptions(vData);
        } catch (err) {
            console.error("Error cargando vouchers:", err);
        }
    }, [filters.start_date, filters.end_date]);

    useEffect(() => {
        loadVouchers();
    }, [loadVouchers]);

    const fetchInsights = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiClient.post('/transport/insights/performance', filters);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al cargar los insights');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const fetchBookingAnalytics = useCallback(async () => {
        setBookingLoading(true);
        setError('');
        try {
            const params = {
                start_date: filters.start_date,
                end_date: filters.end_date
            };
            if (selectedCountry) {
                params.country = selectedCountry;
            }
            const res = await getBookingAnalytics(params);
            setBookingData(res);
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al cargar las analíticas de Booking');
        } finally {
            setBookingLoading(false);
        }
    }, [filters.start_date, filters.end_date, selectedCountry]);

    const fetchBookingQuality = useCallback(async () => {
        setBookingQualityLoading(true);
        setError('');
        setFilterCriticalPendingOnly(false);
        try {
            const params = {};
            if (qualityFilters.start_date) params.start_date = qualityFilters.start_date;
            if (qualityFilters.end_date) params.end_date = qualityFilters.end_date;
            if (qualityFilters.country_code) params.country_code = qualityFilters.country_code;
            if (qualityFilters.zone_name) params.zone_name = qualityFilters.zone_name;
            if (qualityFilters.driver_id) params.driver_id = qualityFilters.driver_id;
            if (qualityFilters.provider_id) params.provider_id = qualityFilters.provider_id;
            if (qualityFilters.status) params.status = qualityFilters.status;
            
            const res = await getQualityInsights(params);
            setBookingQualityData(res);
        } catch (err) {
            setError(err.response?.data?.detail || 'Error al cargar los insights de calidad');
        } finally {
            setBookingQualityLoading(false);
        }
    }, [qualityFilters]);

    const handleSyncSurveys = async () => {
        setIsSyncingSurveys(true);
        setError('');
        try {
            await syncBookingSurveys(7);
            toast.success("Sincronización de encuestas realizada con éxito.");
            fetchBookingQuality();
        } catch (err) {
            setError(err.response?.data?.detail || "Error al sincronizar encuestas con Booking.com.");
        } finally {
            setIsSyncingSurveys(false);
        }
    };

    const handleUpdateIncident = async (tripId, isReviewed, disputeSent = null) => {
        try {
            setUpdatingIncidentId(tripId);
            const payload = { reviewed: isReviewed };
            if (disputeSent !== null) {
                payload.dispute_sent = disputeSent;
            }
            await updateIncidentManagement(tripId, payload);
            toast.success("Incidente actualizado con éxito");
            fetchBookingQuality();
        } catch (error) {
            console.error("Error al actualizar incidente:", error);
            toast.error("Error al actualizar el estado del incidente");
        } finally {
            setUpdatingIncidentId(null);
        }
    };

    const translateIncidentStatus = (status) => {
        if (!status) return 'PENDIENTE';
        const st = status.toUpperCase();
        switch (st) {
            case 'REPORTED':
                return 'REPORTADO';
            case 'RESOLVED':
                return 'RESUELTO';
            case 'RESOLVED_NO_PENALTY':
                return 'RESUELTO SIN MULTA';
            case 'CONFIRMED_WITH_PENALTY':
            case 'PENALIZED':
                return 'MULTADO';
            case 'DISPUTED':
                return 'DISPUTADO';
            default:
                return st;
        }
    };
    const translateIncidentResponsible = (resp) => {
        if (!resp) return 'SIN RESPONSABLE';
        const r = resp.toUpperCase();
        switch (r) {
            case 'SUPPLIER':
                return 'PROVEEDOR';
            case 'CUSTOMER':
                return 'CLIENTE';
            case 'NO_ONE':
                return 'NINGUNO';
            default:
                return r;
        }
    };

    const displayedSurveys = useMemo(() => {
        if (!bookingQualityData || !bookingQualityData.surveys) return [];
        let list = [...bookingQualityData.surveys];
        
        // Ordenar de más antiguo a más reciente
        list.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        
        if (filterCriticalPendingOnly) {
            list = list.filter(s => s.score < 4.6 && s.status === 'PENDING');
        }
        return list;
    }, [bookingQualityData, filterCriticalPendingOnly]);

    const displayedIncidents = useMemo(() => {
        if (!bookingQualityData || !bookingQualityData.incidents) return [];
        let list = [...bookingQualityData.incidents];
        
        // Ordenar de más antiguo a más reciente
        list.sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
        return list;
    }, [bookingQualityData]);

    useEffect(() => {
        if (activeTab === 'booking-quality') {
            fetchBookingQuality();
        }
    }, [activeTab, fetchBookingQuality]);

    const handleExportBookingSearchLogs = async () => {
        setExporting(true);
        try {
            const params = {
                start_date: filters.start_date,
                end_date: filters.end_date
            };
            if (selectedCountry) {
                params.country = selectedCountry;
            }
            const responseData = await exportBookingSearchLogs(params);
            const url = window.URL.createObjectURL(new Blob([responseData]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `booking_search_logs_${filters.start_date}_to_${filters.end_date}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error(err);
            alert("Error al exportar logs de búsqueda.");
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    // Dispersión Locutoras
    const [dispersionData, setDispersionData] = useState(null);
    const [dispersionLoading, setDispersionLoading] = useState(false);
    const [dispersionFilters, setDispersionFilters] = useState({
        start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        dispatcher: 'all',
        base_id: '',
        min_trips: 20
    });
    const [selectedDispatcherDetail, setSelectedDispatcherDetail] = useState(null);

    const fetchDispatcherDispersion = useCallback(async () => {
        setDispersionLoading(true);
        setError('');
        try {
            const params = {
                start_date: dispersionFilters.start_date,
                end_date: dispersionFilters.end_date,
                min_trips: dispersionFilters.min_trips || 20
            };
            if (dispersionFilters.dispatcher && dispersionFilters.dispatcher !== 'all') {
                params.dispatcher = dispersionFilters.dispatcher;
            }
            if (dispersionFilters.base_id) {
                params.base_id = dispersionFilters.base_id;
            }
            const res = await getDispatcherDispersionInsights(params);
            setDispersionData(res);
            setSelectedDispatcherDetail(prev => {
                if (prev) {
                    const match = res.dispatchers?.find(d => d.name === prev.name);
                    return match || res.dispatchers?.[0] || null;
                }
                return res.dispatchers?.[0] || null;
            });
        } catch (err) {
            console.error("Error fetching dispatcher dispersion:", err);
            setError(err.response?.data?.detail || "Error al cargar KPI de dispersión de locutoras.");
        } finally {
            setDispersionLoading(false);
        }
    }, [dispersionFilters]);

    useEffect(() => {
        if (activeTab === 'performance') {
            fetchInsights();
        } else if (activeTab === 'booking') {
            fetchBookingAnalytics();
        } else if (activeTab === 'dispatchers') {
            fetchDispatcherDispersion();
        }
    }, [activeTab, fetchInsights, fetchBookingAnalytics, fetchDispatcherDispersion, selectedCountry]);

    const formatCurrency = (val) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(val);
    };

    const formatCurrencyShort = (val) => {
        if (!val) return '$0';
        if (val >= 1000000) {
            return `$${(val / 1000000).toFixed(1)}M`;
        }
        return `$${Math.round(val / 1000).toLocaleString()}k`;
    };

    const handleBarClick = (barData) => {
        if (!barData) return;
        const payload = barData.payload || barData;
        if (payload && payload.year && payload.month) {
            const year = payload.year;
            const month = payload.month;
            
            const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            const endStr = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
            
            setOriginalDates(prev => {
                if (prev) return prev;
                return {
                    start_date: filters.start_date,
                    end_date: filters.end_date
                };
            });

            setFilters(prev => ({
                ...prev,
                start_date: startStr,
                end_date: endStr
            }));
        }
    };

    const handleResetDates = () => {
        if (originalDates) {
            setFilters(prev => ({
                ...prev,
                start_date: originalDates.start_date,
                end_date: originalDates.end_date
            }));
            setOriginalDates(null);
        }
    };

    const formatDateStr = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}/${parts[1]}/${parts[0]}`;
        }
        return dateStr;
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            const next = { ...prev, [field]: value };
            if (field === 'client_ids') {
                if (value && value.length > 0) {
                    const clientIds = value.map(Number);
                    const validCCIds = costCenterOptions
                        .filter(cc => clientIds.includes(Number(cc.client_id)))
                        .map(cc => cc.id);
                    next.cost_center_ids = (prev.cost_center_ids || []).filter(id => validCCIds.includes(id));

                    const validAreaIds = areaOptions
                        .filter(a => !a.client_id || clientIds.includes(Number(a.client_id)))
                        .map(a => a.id);
                    next.area_ids = (prev.area_ids || []).filter(id => validAreaIds.includes(id));
                }
            }
            return next;
        });
        if (field === 'start_date' || field === 'end_date') {
            setOriginalDates(null);
        }
    };

    const handleMultiSelect = (field, id) => {
        const current = filters[field];
        if (current.includes(id)) {
            handleFilterChange(field, current.filter(item => item !== id));
        } else {
            handleFilterChange(field, [...current, id]);
        }
    };

    if (!data && loading && activeTab === 'performance') return <div className="text-center p-5"><Spinner animation="border" variant="primary" /><h3>Cargando Inteligencia de Negocio...</h3></div>;

    return (
        <Container fluid className="p-0 animate-fade-in">
            {/* PESTAÑAS DE NAVEGACIÓN */}
            <Nav variant="tabs" className="mb-4 fw-bold shadow-sm bg-white rounded" activeKey={activeTab} onSelect={setActiveTab}>
                <Nav.Item>
                    <Nav.Link eventKey="performance" className="py-3 px-4">📈 General (Rentabilidad & KPIs)</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="booking" className="py-3 px-4">🌐 Analíticas Booking.com</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="booking-quality" className="py-3 px-4">⭐ Calidad & Encuestas Booking</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link eventKey="dispatchers" className="py-3 px-4">🎧 Dispersión Locutoras</Nav.Link>
                </Nav.Item>
            </Nav>

            {activeTab === 'performance' ? (
                <>
                    {/* BARRA DE FILTROS AVANZADA */}
                    <Card className="mb-4 shadow-sm border-0 overflow-visible">
                        <Card.Body className="p-3 bg-light rounded">
                            <Row className="align-items-end g-3 mb-3">
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Desde</Form.Label>
                                    <Form.Control type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} size="sm" />
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Hasta</Form.Label>
                                    <Form.Control type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} size="sm" />
                                </Col>
                                <Col xl={4} lg={4} md={4} sm={12}>
                                    <SearchableMultiSelect 
                                        label="Clientes / Convenios"
                                        options={clientOptions}
                                        selected={filters.client_ids}
                                        onChange={(newVal) => handleFilterChange('client_ids', newVal)}
                                        placeholder="Todas las empresas"
                                    />
                                </Col>
                                <Col xl={4} lg={4} md={12} sm={12}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Segmentación</Form.Label>
                                    <div className="d-flex flex-wrap gap-1 border p-1 bg-white rounded" style={{minHeight: '38px'}}>
                                        {segmentations.map(s => (
                                            <Badge 
                                                key={s} 
                                                bg={filters.segmentations.includes(s) ? "primary" : "light"} 
                                                text={filters.segmentations.includes(s) ? "white" : "dark"}
                                                className="cursor-pointer border"
                                                onClick={() => handleMultiSelect('segmentations', s)}
                                                style={{cursor: 'pointer', fontSize: '10px'}}
                                            >
                                                {s}
                                            </Badge>
                                        ))}
                                    </div>
                                </Col>
                            </Row>
                            <Row className="align-items-end g-3 mb-3">
                                <Col xl={3} lg={3} md={6}>
                                    <SearchableMultiSelect 
                                        label="Conductores / Móviles"
                                        options={vehicleOptions}
                                        selected={filters.vehicle_ids}
                                        onChange={(newVal) => handleFilterChange('vehicle_ids', newVal)}
                                        placeholder="Todos los móviles"
                                    />
                                </Col>
                                <Col xl={3} lg={3} md={6}>
                                    <SearchableMultiSelect 
                                        label="Áreas"
                                        options={filteredAreaOptions}
                                        selected={filters.area_ids}
                                        onChange={(newVal) => handleFilterChange('area_ids', newVal)}
                                        placeholder={filters.client_ids?.length > 0 ? "Todas las áreas del cliente" : "Todas las áreas"}
                                    />
                                </Col>
                                <Col xl={3} lg={3} md={6}>
                                    <SearchableMultiSelect 
                                        label="Centros de Costo"
                                        options={filteredCostCenterOptions}
                                        selected={filters.cost_center_ids}
                                        onChange={(newVal) => handleFilterChange('cost_center_ids', newVal)}
                                        placeholder={filters.client_ids?.length > 0 ? "Todos los CC del cliente" : "Todos los CC"}
                                    />
                                </Col>
                                <Col xl={3} lg={3} md={6}>
                                    <SearchableMultiSelect 
                                        label="Vouchers"
                                        options={voucherOptions}
                                        selected={filters.vouchers}
                                        onChange={(newVal) => handleFilterChange('vouchers', newVal)}
                                        placeholder="Todos los vouchers"
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-end g-3 mb-3">
                                <Col xl={6} lg={6} md={6}>
                                    <SearchableMultiSelect 
                                        label="País"
                                        options={countryOptions}
                                        selected={filters.countries}
                                        onChange={handleCountryChange}
                                        placeholder="Todos los países"
                                    />
                                </Col>
                                <Col xl={6} lg={6} md={6}>
                                    <SearchableMultiSelect 
                                        label="Zona / Base"
                                        options={baseOptionsList}
                                        selected={filters.cities}
                                        onChange={(newVal) => handleFilterChange('cities', newVal)}
                                        placeholder="Todas las zonas/bases"
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-end g-3">
                                <Col xl={8} lg={8} md={8}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Comparar contra</Form.Label>
                                    <Form.Select value={filters.compare_to} onChange={(e) => handleFilterChange('compare_to', e.target.value)} size="sm">
                                        <option value="previous_year">Mismo periodo (Año Anterior)</option>
                                        <option value="previous_period">Periodo Inmediato Anterior</option>
                                    </Form.Select>
                                </Col>
                                <Col xl={4} lg={4} md={4}>
                                    <Button variant="primary" className="w-100 fw-bold" size="sm" onClick={fetchInsights} disabled={loading}>
                                        {loading ? <Spinner size="sm" animation="border" /> : <><Filter size={14} className="me-2"/> Generar Reporte</>}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {error && <Alert variant="danger">{error}</Alert>}

                    {data && (
                        <>
                            {/* BIG NUMBERS */}
                            <Row className="mb-4 g-3 row-cols-1 row-cols-md-5">
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-primary border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">VENTA TOTAL</div>
                                        <h3 className="fw-bold mb-1 text-primary">{formatCurrency(data.summary.total_sales)}</h3>
                                        <div className={`small fw-bold ${data.summary.growth_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {data.summary.growth_pct >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {data.summary.growth_pct.toFixed(1)}% vs anterior
                                        </div>
                                        <div className="text-muted small mt-1" style={{ fontSize: '0.78rem' }}>
                                            Ant: <span className="fw-semibold text-dark">{formatCurrency(data.summary.prev_sales ?? 0)}</span>
                                        </div>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-success border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">UTILIDAD BRUTA</div>
                                        <h3 className="fw-bold mb-1 text-success">{formatCurrency(data.summary.total_profit)}</h3>
                                        <div className={`small fw-bold mb-1 ${(data.summary.profit_growth_pct ?? 0) >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {(data.summary.profit_growth_pct ?? 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {(data.summary.profit_growth_pct ?? 0).toFixed(1)}% vs anterior
                                        </div>
                                        <div className="text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                                            Ant: <span className="fw-semibold text-dark">{formatCurrency(data.summary.prev_profit ?? 0)}</span>
                                        </div>
                                        <div className="text-muted fw-bold pt-2 mt-auto border-top" style={{ fontSize: '0.75rem' }}>
                                            Margen: {(data.summary.current_margin_pct ?? (data.summary.total_sales > 0 ? (data.summary.total_profit / data.summary.total_sales * 100) : 0)).toFixed(1)}%
                                            <span className="text-muted fw-normal ms-1">
                                                (Ant: {(data.summary.prev_margin_pct ?? 0).toFixed(1)}% | 
                                                <span className={(data.summary.margin_diff ?? 0) >= 0 ? 'text-success fw-bold' : 'text-danger fw-bold'}>
                                                    {' '}{(data.summary.margin_diff ?? 0) >= 0 ? '+' : ''}{(data.summary.margin_diff ?? 0).toFixed(1)} pp
                                                </span>)
                                            </span>
                                        </div>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-dark border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">COSTO (PAGO CHOFER)</div>
                                        <h3 className="fw-bold mb-1 text-dark">{formatCurrency(data.summary.total_costs)}</h3>
                                        <div className={`small fw-bold mb-1 ${(data.summary.costs_growth_pct ?? 0) <= 0 ? 'text-success' : 'text-danger'}`}>
                                            {(data.summary.costs_growth_pct ?? 0) >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {(data.summary.costs_growth_pct ?? 0).toFixed(1)}% vs anterior
                                        </div>
                                        <div className="text-muted small mb-1" style={{ fontSize: '0.78rem' }}>
                                            Ant: <span className="fw-semibold text-dark">{formatCurrency(data.summary.prev_costs ?? 0)}</span>
                                        </div>
                                        <div className="text-muted pt-2 mt-auto border-top" style={{ fontSize: '0.75rem' }}>
                                            {(data.summary.current_cost_ratio ?? (data.summary.total_sales > 0 ? (data.summary.total_costs / data.summary.total_sales * 100) : 0)).toFixed(1)}% del ingreso
                                            <span className="text-muted ms-1">(Ant: {(data.summary.prev_cost_ratio ?? 0).toFixed(1)}%)</span>
                                        </div>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-info border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">CANT. SERVICIOS</div>
                                        <h3 className="fw-bold mb-1 text-info">{data.summary.total_services || 0}</h3>
                                        <div className={`small fw-bold ${data.summary.services_growth_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {data.summary.services_growth_pct >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {data.summary.services_growth_pct.toFixed(1)}% vs anterior
                                        </div>
                                        <div className="text-muted small mt-1" style={{ fontSize: '0.78rem' }}>
                                            Ant: <span className="fw-semibold text-dark">{data.summary.prev_services || 0} viajes</span>
                                        </div>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-secondary border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">TICKET PROMEDIO</div>
                                        <h3 className="fw-bold mb-1 text-secondary">{formatCurrency(data.summary.avg_ticket || 0)}</h3>
                                        <div className={`small fw-bold ${data.summary.avg_ticket_growth_pct >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {data.summary.avg_ticket_growth_pct >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />} {data.summary.avg_ticket_growth_pct.toFixed(1)}% vs anterior
                                        </div>
                                        <div className="text-muted small mt-1" style={{ fontSize: '0.78rem' }}>
                                            Ant: <span className="fw-semibold text-dark">{formatCurrency(data.summary.prev_avg_ticket || 0)}</span>
                                        </div>
                                    </Card>
                                </Col>
                            </Row>

                            {/* GRÁFICO DE RENDIMIENTO */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h5 className="mb-0 fw-bold">Flujo de Caja Mensual: Ingresos vs Costos</h5>
                                    {originalDates && (
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            className="d-flex align-items-center gap-1 fw-bold"
                                            onClick={handleResetDates}
                                        >
                                            🔄 Ver todos los meses ({formatDateStr(originalDates.start_date)} a {formatDateStr(originalDates.end_date)})
                                        </Button>
                                    )}
                                </Card.Header>
                                <Card.Body>
                                    <div style={{ width: '100%', height: 400 }}>
                                        <ResponsiveContainer>
                                            <ComposedChart data={data.monthly_series}>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                                <XAxis dataKey="label" axisLine={false} tickLine={false} />
                                                <YAxis yAxisId="left" axisLine={false} tickLine={false} tickFormatter={(v) => `$${v/1000000}M`} />
                                                <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, 100]} axisLine={false} tickLine={false} />
                                                <Tooltip 
                                                    formatter={(value, name) => [
                                                        (name.toLowerCase().includes('margen') || name === 'margin_pct') 
                                                            ? `${value}%` 
                                                            : formatCurrency(value), 
                                                        name.toUpperCase()
                                                    ]}
                                                    contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                                />
                                                <Legend verticalAlign="top" align="right" height={36}/>
                                                <Bar 
                                                    yAxisId="left" 
                                                    dataKey="costs" 
                                                    name="Costo (Pago)" 
                                                    stackId="a" 
                                                    fill="#343a40" 
                                                    radius={[0, 0, 0, 0]} 
                                                    barSize={45} 
                                                    onClick={(barData) => handleBarClick(barData)}
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                <Bar 
                                                    yAxisId="left" 
                                                    dataKey="profit" 
                                                    name="Utilidad" 
                                                    stackId="a" 
                                                    fill="#28a745" 
                                                    radius={[5, 5, 0, 0]} 
                                                    barSize={45} 
                                                    onClick={(barData) => handleBarClick(barData)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <LabelList 
                                                        dataKey="sales" 
                                                        position="top" 
                                                        formatter={(val) => formatCurrencyShort(val)} 
                                                        style={{ fill: '#495057', fontSize: 10, fontWeight: 'bold' }} 
                                                    />
                                                </Bar>
                                                <Line 
                                                    yAxisId="right" 
                                                    type="monotone" 
                                                    dataKey="margin_pct" 
                                                    name="Margen %" 
                                                    stroke="#ffc107" 
                                                    strokeWidth={4} 
                                                    dot={{ r: 6, fill: '#ffc107', strokeWidth: 2, stroke: '#fff' }}
                                                    activeDot={{ r: 8 }}
                                                />
                                            </ComposedChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* GRÁFICO DE RENDIMIENTO DIARIO */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <h5 className="mb-0 fw-bold">Flujo de Caja Diario: Ingresos vs Costos</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div style={{ width: '100%', height: 400 }}>
                                        {data.daily_series && data.daily_series.length > 0 ? (
                                            <ResponsiveContainer>
                                                <ComposedChart data={data.daily_series}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                                    <XAxis dataKey="label" axisLine={false} tickLine={false} />
                                                    <YAxis yAxisId="left" axisLine={false} tickLine={false} tickFormatter={(v) => formatCurrencyShort(v)} />
                                                    <YAxis yAxisId="right" orientation="right" unit="%" domain={[0, 100]} axisLine={false} tickLine={false} />
                                                    <Tooltip 
                                                        formatter={(value, name) => [
                                                            (name.toLowerCase().includes('margen') || name === 'margin_pct') 
                                                                ? `${value}%` 
                                                                : formatCurrency(value), 
                                                            name.toUpperCase()
                                                        ]}
                                                        contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                                                    />
                                                    <Legend verticalAlign="top" align="right" height={36}/>
                                                    <Bar 
                                                        yAxisId="left" 
                                                        dataKey="costs" 
                                                        name="Costo (Pago)" 
                                                        stackId="a" 
                                                        fill="#343a40" 
                                                        radius={[0, 0, 0, 0]} 
                                                        barSize={15} 
                                                    />
                                                    <Bar 
                                                        yAxisId="left" 
                                                        dataKey="profit" 
                                                        name="Utilidad" 
                                                        stackId="a" 
                                                        fill="#28a745" 
                                                        radius={[3, 3, 0, 0]} 
                                                        barSize={15} 
                                                    >
                                                        <LabelList 
                                                            dataKey="sales" 
                                                            position="top" 
                                                            formatter={(val) => formatCurrencyShort(val)} 
                                                            style={{ fill: '#495057', fontSize: 9, fontWeight: 'bold' }} 
                                                        />
                                                    </Bar>
                                                    <Line 
                                                        yAxisId="right" 
                                                        type="monotone" 
                                                        dataKey="margin_pct" 
                                                        name="Margen %" 
                                                        stroke="#ffc107" 
                                                        strokeWidth={3} 
                                                        dot={{ r: 4, fill: '#ffc107', strokeWidth: 2, stroke: '#fff' }}
                                                        activeDot={{ r: 6 }}
                                                    />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center h-100">
                                                <span className="text-muted">No hay datos diarios disponibles para el periodo seleccionado</span>
                                            </div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* TABLA DE CLIENTES Y COMPARATIVA */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
                                    <h5 className="mb-0 fw-bold">Ranking de Rentabilidad por {viewMode === 'client' ? 'Cliente' : 'Holding'}</h5>
                                    <div className="d-flex align-items-center gap-3">
                                        {data.holding_table && data.holding_table.length > 0 && (
                                            <ButtonGroup size="sm" className="shadow-sm">
                                                <Button 
                                                    variant={viewMode === 'client' ? 'primary' : 'outline-primary'} 
                                                    onClick={() => setViewMode('client')}
                                                    className="fw-bold"
                                                >
                                                    Empresas (RUT)
                                                </Button>
                                                <Button 
                                                    variant={viewMode === 'holding' ? 'primary' : 'outline-primary'} 
                                                    onClick={() => setViewMode('holding')}
                                                    className="fw-bold"
                                                >
                                                    Holdings (Consolidado)
                                                </Button>
                                            </ButtonGroup>
                                        )}
                                        <Badge bg="light" text="dark" className="border px-3 py-2">Tabla Interactiva (Ordenable por columnas)</Badge>
                                    </div>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table hover responsive className="mb-0 align-middle border-top">
                                        <thead className="bg-light">
                                            <tr className="small text-uppercase">
                                                <th className="ps-4 py-3 cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('client_name')}>
                                                    {viewMode === 'client' ? 'CLIENTE' : 'HOLDING'} {renderSortIcon('client_name')}
                                                </th>
                                                <th className="text-end cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('current_services')}>
                                                    SERV. ACTUAL {renderSortIcon('current_services')}
                                                </th>
                                                <th className="text-end cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('previous_services')}>
                                                    SERV. ANTERIOR {renderSortIcon('previous_services')}
                                                </th>
                                                <th className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('services_growth_pct')}>
                                                    CREC. SERV. % {renderSortIcon('services_growth_pct')}
                                                </th>
                                                <th className="text-end cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('current_sales')}>
                                                    VENTA ACTUAL {renderSortIcon('current_sales')}
                                                </th>
                                                <th className="text-end cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('previous_sales')}>
                                                    VENTA ANTERIOR {renderSortIcon('previous_sales')}
                                                </th>
                                                <th className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sales_growth_pct')}>
                                                    CREC. VENTA % {renderSortIcon('sales_growth_pct')}
                                                </th>
                                                <th className="text-end cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('current_profit')}>
                                                    UTILIDAD {renderSortIcon('current_profit')}
                                                </th>
                                                <th className="text-center cursor-pointer user-select-none" style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('current_margin_pct')}>
                                                    MARGEN % {renderSortIcon('current_margin_pct')}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedTableData.map((c, i) => (
                                                <tr key={i}>
                                                    <td className="ps-4 fw-bold text-uppercase" style={{fontSize: '0.85rem'}}>{c.client_name}</td>
                                                    <td className="text-end fw-bold" style={{fontSize: '0.9rem'}}>{c.current_services}</td>
                                                    <td className="text-end text-muted small" style={{fontSize: '0.85rem'}}>{c.previous_services}</td>
                                                    <td className="text-center">
                                                        <Badge bg={c.services_growth_pct >= 0 ? "success" : "danger"} className="rounded-pill px-2 py-1" style={{minWidth: '60px', fontSize: '10px'}}>
                                                            {c.services_growth_pct > 0 ? '+' : ''}{c.services_growth_pct.toFixed(1)}%
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end fw-bold" style={{fontSize: '0.9rem'}}>{formatCurrency(c.current_sales)}</td>
                                                    <td className="text-end text-muted small" style={{fontSize: '0.85rem'}}>{formatCurrency(c.previous_sales)}</td>
                                                    <td className="text-center">
                                                        <Badge bg={c.sales_growth_pct >= 0 ? "success" : "danger"} className="rounded-pill px-2 py-1" style={{minWidth: '60px', fontSize: '10px'}}>
                                                            {c.sales_growth_pct > 0 ? '+' : ''}{c.sales_growth_pct.toFixed(1)}%
                                                        </Badge>
                                                    </td>
                                                    <td className="text-end" style={{fontSize: '0.9rem'}}>
                                                        <div className="text-success fw-bold">{formatCurrency(c.current_profit)}</div>
                                                        <div className="text-muted fw-normal" style={{fontSize: '0.75rem'}}>
                                                            Ant: {formatCurrency(c.previous_profit)}
                                                        </div>
                                                        <div className={`fw-bold ${c.profit_growth_pct >= 0 ? 'text-success' : 'text-danger'}`} style={{fontSize: '10px'}}>
                                                            {c.profit_growth_pct >= 0 ? `▲ +${c.profit_growth_pct.toFixed(1)}%` : `▼ ${c.profit_growth_pct.toFixed(1)}%`}
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="fw-bold text-dark" style={{fontSize: '0.9rem'}}>{c.current_margin_pct.toFixed(1)}%</div>
                                                        <div className="text-muted" style={{fontSize: '0.75rem'}}>
                                                            Ant: {(c.previous_margin_pct ?? (c.previous_sales > 0 ? (c.previous_profit / c.previous_sales * 100) : 0)).toFixed(1)}%
                                                        </div>
                                                        {(() => {
                                                            const prevMargin = c.previous_margin_pct ?? (c.previous_sales > 0 ? (c.previous_profit / c.previous_sales * 100) : 0);
                                                            const mDiff = c.margin_diff ?? (c.current_margin_pct - prevMargin);
                                                            return (
                                                                <div className={`fw-bold ${mDiff >= 0 ? 'text-success' : 'text-danger'}`} style={{fontSize: '10px'}}>
                                                                    {mDiff >= 0 ? `+${mDiff.toFixed(1)} pp` : `${mDiff.toFixed(1)} pp`}
                                                                </div>
                                                            );
                                                        })()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </>
            ) : activeTab === 'booking' ? (
                <>
                    {/* NAV DE SUB-PESTAÑAS DE BOOKING */}
                    <Nav variant="pills" className="mb-4 fw-bold shadow-sm bg-white p-2 rounded border" activeKey={bookingSubTab} onSelect={setBookingSubTab}>
                        <Nav.Item>
                            <Nav.Link eventKey="overview" className="py-2 px-3">📊 Resumen KPIs</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="provider-demand" className="py-2 px-3">🚐 Demanda Proveedores</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="logs" className="py-2 px-3">📋 Consultas Detalladas</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="heatmap" className="py-2 px-3">🗺️ Modo Mapa & Polígonos</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="sentinel" className="py-2 px-3">🤖 Centinela (Competencia)</Nav.Link>
                        </Nav.Item>
                    </Nav>

                    {bookingSubTab === 'provider-demand' ? (
                        <BookingProviderInsightsView 
                            driverOptions={driverOptions}
                            providerOptions={providerOptions}
                        />
                    ) : bookingSubTab === 'logs' ? (
                        <BookingSearchLogsView bases={bases} />
                    ) : bookingSubTab === 'heatmap' ? (
                        <BookingHeatmapView bases={bases} />
                    ) : bookingSubTab === 'sentinel' ? (
                        <BookingSentinelView />
                    ) : (
                        <>
                    {/* BARRA DE FILTROS SIMPLE PARA BOOKING */}
                    <Card className="mb-4 shadow-sm border-0">
                        <Card.Body className="p-3 bg-light rounded">
                            <Row className="align-items-end g-3">
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Desde</Form.Label>
                                    <Form.Control type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} size="sm" />
                                </Col>
                                <Col md={3}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Hasta</Form.Label>
                                    <Form.Control type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} size="sm" />
                                </Col>
                                <Col md={3}>
                                    <Button variant="primary" className="w-100 fw-bold" size="sm" onClick={fetchBookingAnalytics} disabled={bookingLoading}>
                                        {bookingLoading ? <Spinner size="sm" animation="border" /> : <><Filter size={14} className="me-2"/> Generar Reporte</>}
                                    </Button>
                                </Col>
                                <Col md={3}>
                                    <Button variant="outline-success" className="w-100 fw-bold" size="sm" onClick={handleExportBookingSearchLogs} disabled={exporting || !bookingData}>
                                        {exporting ? <Spinner size="sm" animation="border" /> : "📥 Descargar Excel (CSV)"}
                                    </Button>
                                </Col>
                            </Row>
                            <hr className="my-3" />
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                <span className="small fw-bold text-muted text-uppercase me-2">Filtrar por País:</span>
                                <ButtonGroup size="sm" className="shadow-sm">
                                    <Button 
                                        variant={selectedCountry === '' ? 'primary' : 'outline-primary'} 
                                        onClick={() => setSelectedCountry('')}
                                        className="fw-bold"
                                    >
                                        🌐 Todos
                                    </Button>
                                    <Button 
                                        variant={selectedCountry === 'Chile' ? 'primary' : 'outline-primary'} 
                                        onClick={() => setSelectedCountry('Chile')}
                                        className="fw-bold"
                                    >
                                        🇨🇱 Chile
                                    </Button>
                                    <Button 
                                        variant={selectedCountry === 'Argentina' ? 'primary' : 'outline-primary'} 
                                        onClick={() => setSelectedCountry('Argentina')}
                                        className="fw-bold"
                                    >
                                        🇦🇷 Argentina
                                    </Button>
                                    <Button 
                                        variant={selectedCountry === 'Peru' ? 'primary' : 'outline-primary'} 
                                        onClick={() => setSelectedCountry('Peru')}
                                        className="fw-bold"
                                    >
                                        🇵🇪 Perú
                                    </Button>
                                    <Button 
                                        variant={selectedCountry === 'Dominican Republic' ? 'primary' : 'outline-primary'} 
                                        onClick={() => setSelectedCountry('Dominican Republic')}
                                        className="fw-bold"
                                    >
                                        🇩🇴 Rep. Dominicana
                                    </Button>
                                    <Button 
                                        variant={selectedCountry === 'Brasil' ? 'primary' : 'outline-primary'} 
                                        onClick={() => setSelectedCountry('Brasil')}
                                        className="fw-bold"
                                    >
                                        🇧🇷 Brasil
                                    </Button>
                                </ButtonGroup>
                            </div>
                        </Card.Body>
                    </Card>

                    {error && <Alert variant="danger">{error}</Alert>}

                    {bookingLoading && !bookingData && (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="success" />
                            <h4 className="mt-3">Procesando analíticas de la API de Booking.com...</h4>
                        </div>
                    )}

                    {bookingData && (
                        <>
                            {/* KPI CARDS BOOKING */}
                            <Row className="mb-4 g-3 row-cols-1 row-cols-md-4">
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-primary border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">BÚSQUEDAS TOTALES (API)</div>
                                        <h3 className="fw-bold mb-0 text-primary">{bookingData.summary.total_searches.toLocaleString()}</h3>
                                        <small className="text-muted">Consultas de cotización recibidas</small>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-success border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">RESERVAS EMIGO</div>
                                        <h3 className="fw-bold mb-0 text-success">{bookingData.summary.total_bookings.toLocaleString()}</h3>
                                        <small className="text-muted">Servicios Booking creados</small>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-info border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">TASA DE CONVERSIÓN</div>
                                        <h3 className="fw-bold mb-0 text-info">{bookingData.summary.conversion_rate.toFixed(1)}%</h3>
                                        <small className="text-muted">Búsquedas que terminaron en reserva</small>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-warning border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">SIN DISPONIBILIDAD</div>
                                        <h3 className="fw-bold mb-0 text-warning">{bookingData.summary.no_availability_searches.toLocaleString()}</h3>
                                        <small className="text-muted">Tasa de rechazo: {bookingData.summary.no_availability_rate.toFixed(1)}%</small>
                                    </Card>
                                </Col>
                            </Row>

                            {/* GRÁFICO TEMPORAL DE BÚSQUEDAS */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white border-0 py-3">
                                    <h5 className="mb-0 fw-bold">Evolución de Consultas Diarias en Booking.com</h5>
                                </Card.Header>
                                <Card.Body>
                                    <div style={{ width: '100%', height: 350 }}>
                                        {bookingData.timeline && bookingData.timeline.length > 0 ? (
                                            <ResponsiveContainer>
                                                <ComposedChart data={bookingData.timeline}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                                    <XAxis dataKey="date" axisLine={false} tickLine={false} />
                                                    <YAxis axisLine={false} tickLine={false} />
                                                    <Tooltip contentStyle={{ borderRadius: '10px', border: 'none', boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }} />
                                                    <Legend verticalAlign="top" align="right" height={36}/>
                                                    <Bar dataKey="searches" name="Búsquedas Totales" fill="#0071c2" radius={[4, 4, 0, 0]} barSize={35} />
                                                    <Bar dataKey="no_avail" name="Sin Disponibilidad" fill="#fd7e14" radius={[4, 4, 0, 0]} barSize={35} />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div className="text-center p-5 text-muted">No hay datos históricos de búsquedas para este rango.</div>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* TABLA DE RUTAS CALIENTES */}
                            <Row>
                                <Col md={12}>
                                    <Card className="border-0 shadow-sm mb-4">
                                        <Card.Header className="bg-white border-0 py-3">
                                            <h5 className="mb-0 fw-bold">Rutas Más Solicitadas por Clientes en Booking.com</h5>
                                        </Card.Header>
                                        <Card.Body className="p-0">
                                            <Table hover responsive className="mb-0 align-middle border-top">
                                                <thead className="bg-light">
                                                    <tr className="small text-uppercase">
                                                        <th className="ps-4 py-3" style={{width: '140px'}}>Fecha Servicio</th>
                                                        <th>Dirección Origen</th>
                                                        <th>Dirección Destino</th>
                                                        <th className="text-center" style={{width: '180px'}}>Consultas Realizadas</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {bookingData.top_routes && bookingData.top_routes.length > 0 ? (
                                                        bookingData.top_routes.map((route, idx) => (
                                                            <tr key={idx}>
                                                                <td className="ps-4 small fw-bold text-primary">{route.date ? formatDateStr(route.date) : 'S/F'}</td>
                                                                <td className="fw-bold x-small text-uppercase">{route.origin}</td>
                                                                <td className="fw-bold x-small text-uppercase text-muted">{route.destination}</td>
                                                                <td className="text-center">
                                                                    <Badge bg="primary" className="rounded-pill px-3 py-2 fw-bold" style={{fontSize: '11px'}}>
                                                                        {route.count.toLocaleString()}
                                                                    </Badge>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan={4} className="text-center p-4 text-muted">No se registraron rutas consultadas en este periodo.</td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </>
                    )}
                        </>
                    )}
                </>
            ) : activeTab === 'booking-quality' ? (
                <>
                    {/* CALIDAD Y ENCUESTAS BOOKING (booking-quality) */}
                    <Card className="mb-4 shadow-sm border-0">
                        <Card.Body className="p-3 bg-light rounded">
                            <Row className="align-items-end g-3">
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Desde</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={qualityFilters.start_date} 
                                        onChange={(e) => setQualityFilters(prev => ({ ...prev, start_date: e.target.value }))} 
                                        size="sm" 
                                    />
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Hasta</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={qualityFilters.end_date} 
                                        onChange={(e) => setQualityFilters(prev => ({ ...prev, end_date: e.target.value }))} 
                                        size="sm" 
                                    />
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">País</Form.Label>
                                    <Form.Select 
                                        value={qualityFilters.country_code} 
                                        onChange={(e) => setQualityFilters(prev => ({ ...prev, country_code: e.target.value }))} 
                                        size="sm"
                                    >
                                        <option value="">🌐 Todos</option>
                                        <option value="CL">🇨🇱 Chile</option>
                                        <option value="AR">🇦🇷 Argentina</option>
                                        <option value="PE">🇵🇪 Perú</option>
                                        <option value="DO">🇩🇴 Rep. Dominicana</option>
                                        <option value="BR">🇧🇷 Brasil</option>
                                    </Form.Select>
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Zona / Ciudad</Form.Label>
                                    <SearchableSelect
                                        options={zoneOptionsList}
                                        value={qualityFilters.zone_name}
                                        onChange={(val) => setQualityFilters(prev => ({ ...prev, zone_name: val }))}
                                        placeholder="📍 Todas"
                                    />
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Conductor</Form.Label>
                                    <SearchableSelect
                                        options={driverOptions}
                                        value={qualityFilters.driver_id}
                                        onChange={(val) => setQualityFilters(prev => ({ ...prev, driver_id: val }))}
                                        placeholder="👥 Todos"
                                    />
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Proveedor</Form.Label>
                                    <SearchableSelect
                                        options={providerOptions}
                                        value={qualityFilters.provider_id}
                                        onChange={(val) => setQualityFilters(prev => ({ ...prev, provider_id: val }))}
                                        placeholder="🏢 Todos"
                                    />
                                </Col>
                            </Row>
                            <Row className="align-items-end g-3 mt-1">
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Estado Gestión</Form.Label>
                                    <Form.Select 
                                        value={qualityFilters.status} 
                                        onChange={(e) => setQualityFilters(prev => ({ ...prev, status: e.target.value }))} 
                                        size="sm"
                                    >
                                        <option value="">📋 Todos</option>
                                        <option value="PENDING">🟠 Pendiente</option>
                                        <option value="REVIEWED">🟢 Gestionado</option>
                                    </Form.Select>
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Button variant="primary" className="w-100 fw-bold" size="sm" onClick={fetchBookingQuality} disabled={bookingQualityLoading}>
                                        {bookingQualityLoading ? <Spinner size="sm" animation="border" /> : <><Filter size={14} className="me-2"/> Filtrar</>}
                                    </Button>
                                </Col>
                                <Col xl={2} lg={2} md={4} sm={6}>
                                    <Button variant="outline-danger" className="w-100 fw-bold" size="sm" onClick={handleSyncSurveys} disabled={isSyncingSurveys || bookingQualityLoading}>
                                        {isSyncingSurveys ? <Spinner size="sm" animation="border" /> : <>🔄 Sincronizar Encuestas</>}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {error && <Alert variant="danger">{error}</Alert>}

                    {bookingQualityLoading && !bookingQualityData && (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <h4 className="mt-3">Procesando insights de calidad de Booking.com...</h4>
                        </div>
                    )}

                    {bookingQualityData && (
                        <>
                            {/* KPI CARDS QUALITY */}
                            <Row className="mb-4 g-3 row-cols-1 row-cols-md-4">
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-primary border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">PROMEDIO GENERAL</div>
                                        <h3 className={`fw-bold mb-0 ${
                                            bookingQualityData.kpis.average_score >= 4.6 ? 'text-success' : 
                                            bookingQualityData.kpis.average_score >= 4.0 ? 'text-warning' : 'text-danger'
                                        }`}>
                                            ⭐ {bookingQualityData.kpis.average_score.toFixed(2)}
                                        </h3>
                                        <small className="text-muted">Meta partner: 4.6+</small>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-success border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">CUMPLIMIENTO SLA</div>
                                        <h3 className="fw-bold mb-0 text-success">{bookingQualityData.kpis.sla_percentage}%</h3>
                                        <small className="text-muted">Calificaciones >= 4.6</small>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card className="border-0 shadow-sm text-center p-3 h-100 border-start border-info border-4">
                                        <div className="text-muted small fw-bold mb-1 text-uppercase">TOTAL EVALUACIONES</div>
                                        <h3 className="fw-bold mb-0 text-info">{bookingQualityData.kpis.total_evaluations}</h3>
                                        <small className="text-muted">Respuestas en el periodo</small>
                                    </Card>
                                </Col>
                                <Col>
                                    <Card 
                                        className={`border-0 shadow-sm text-center p-3 h-100 border-start border-warning border-4 transition-all duration-200 ${
                                            filterCriticalPendingOnly ? 'bg-danger bg-opacity-10 border-danger shadow' : ''
                                        }`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setFilterCriticalPendingOnly(!filterCriticalPendingOnly)}
                                    >
                                        <div className="text-muted small fw-bold mb-1 text-uppercase d-flex justify-content-center align-items-center gap-2">
                                            CRÍTICOS PENDIENTES
                                            {filterCriticalPendingOnly && (
                                                <Badge bg="danger" className="rounded-pill" style={{ fontSize: '9px' }}>
                                                    Activo ✕
                                                </Badge>
                                            )}
                                        </div>
                                        <h3 className={`fw-bold mb-0 ${bookingQualityData.kpis.critical_pending_count > 0 ? 'text-danger animate-pulse' : 'text-muted'}`}>
                                            {bookingQualityData.kpis.critical_pending_count}
                                        </h3>
                                        <small className="text-muted">
                                            {filterCriticalPendingOnly ? "Click para mostrar todos" : "Click para filtrar notas < 4.6"}
                                        </small>
                                    </Card>
                                </Col>
                            </Row>

                            {/* SCORECARD CUMPLIMIENTO BOOKING */}
                            {bookingQualityData.scorecard && (
                                <Card className="border-0 shadow-sm mb-4">
                                    <Card.Header className="bg-white border-0 py-3 d-flex justify-content-between align-items-center">
                                        <h5 className="mb-0 fw-bold">🏆 Tarjeta de Cumplimiento Partner (Booking.com Scorecard)</h5>
                                        <Badge bg="dark" text="white" className="px-2 py-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                            Targets de Calidad Oficiales
                                        </Badge>
                                    </Card.Header>
                                    <Card.Body className="pt-0">
                                        <Row className="g-3">
                                            <Col md={4} sm={12}>
                                                <Card className={`text-center p-3 h-100 border-0 ${bookingQualityData.scorecard.average_score_sla_met ? 'bg-success bg-opacity-10 border-start border-4 border-success' : 'bg-danger bg-opacity-10 border-start border-4 border-danger'}`}>
                                                    <div className="text-muted small fw-bold mb-1 text-uppercase" style={{ fontSize: '10px' }}>PROMEDIO DE OPINIÓN</div>
                                                    <h3 className={`fw-black mb-1 ${bookingQualityData.scorecard.average_score_sla_met ? 'text-success' : 'text-danger'}`}>
                                                        ⭐ {bookingQualityData.scorecard.average_score.toFixed(2)}
                                                    </h3>
                                                    <div className="small font-monospace text-muted">Target: &gt;= 4.60</div>
                                                    <div className="x-small text-muted mt-1">({bookingQualityData.scorecard.total_trips} viajes totales en periodo)</div>
                                                </Card>
                                            </Col>
                                            <Col md={4} sm={12}>
                                                <Card className={`text-center p-3 h-100 border-0 ${bookingQualityData.scorecard.incident_rate_sla_met ? 'bg-success bg-opacity-10 border-start border-4 border-success' : 'bg-danger bg-opacity-10 border-start border-4 border-danger'}`}>
                                                    <div className="text-muted small fw-bold mb-1 text-uppercase" style={{ fontSize: '10px' }}>TASA DE INCIDENTES (PROVEEDOR)</div>
                                                    <h3 className={`fw-black mb-1 ${bookingQualityData.scorecard.incident_rate_sla_met ? 'text-success' : 'text-danger'}`}>
                                                        🚨 {bookingQualityData.scorecard.incident_rate.toFixed(2)}%
                                                    </h3>
                                                    <div className="small font-monospace text-muted">Target: &lt;= 1.50%</div>
                                                    <div className="x-small text-muted mt-1">({bookingQualityData.scorecard.incident_count} incidentes de {bookingQualityData.scorecard.total_trips} viajes)</div>
                                                </Card>
                                            </Col>
                                            <Col md={4} sm={12}>
                                                <Card className={`text-center p-3 h-100 border-0 ${bookingQualityData.scorecard.driver_events_rate_sla_met ? 'bg-success bg-opacity-10 border-start border-4 border-success' : 'bg-danger bg-opacity-10 border-start border-4 border-danger'}`}>
                                                    <div className="text-muted small fw-bold mb-1 text-uppercase" style={{ fontSize: '10px' }}>TASA DE EVENTOS DEL CHOFER</div>
                                                    <h3 className={`fw-black mb-1 ${bookingQualityData.scorecard.driver_events_rate_sla_met ? 'text-success' : 'text-danger'}`}>
                                                        📱 {bookingQualityData.scorecard.driver_events_rate.toFixed(1)}%
                                                    </h3>
                                                    <div className="small font-monospace text-muted">Target: &gt;= 80.0%</div>
                                                    <div className="x-small text-muted mt-1">({bookingQualityData.scorecard.completed_with_events} de {bookingQualityData.scorecard.total_completed} completados con GPS/Arrived)</div>
                                                </Card>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* TABLA DE DETALLES */}
                            <Card className="border-0 shadow-sm mb-4">
                                <Card.Header className="bg-white border-0 p-0">
                                    <Nav variant="tabs" activeKey={qualityTab} onSelect={(k) => setQualityTab(k)} className="border-0 px-3 pt-3">
                                        <Nav.Item>
                                            <Nav.Link eventKey="surveys" className="fw-bold border-bottom-0">
                                                ⭐ Encuestas de Satisfacción ({displayedSurveys.length})
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link eventKey="incidents" className="fw-bold border-bottom-0">
                                                🚨 Incidentes de Booking.com ({bookingQualityData.incidents?.length || 0})
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    {qualityTab === 'surveys' && (
                                        <Table hover responsive className="mb-0 align-middle border-top">
                                            <thead className="bg-light">
                                                <tr className="small text-uppercase">
                                                    <th className="ps-4 py-3" style={{ width: '120px' }}>Fecha</th>
                                                    <th style={{ width: '150px' }}>Reserva / OS</th>
                                                    <th>Conductor</th>
                                                    <th>Proveedor</th>
                                                    <th>Zona</th>
                                                    <th style={{ width: '90px' }}>Nota</th>
                                                    <th>Comentario Cliente</th>
                                                    <th style={{ width: '110px' }}>Estado</th>
                                                    <th style={{ width: '100px' }} className="text-center">Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {displayedSurveys && displayedSurveys.length > 0 ? (
                                                    displayedSurveys.map((s, idx) => (
                                                        <tr key={idx}>
                                                            <td className="ps-4 small text-muted">{formatDateStr(s.scheduled_time.split('T')[0])}</td>
                                                            <td>
                                                                <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>#{s.id}</div>
                                                                <div className="small text-muted font-monospace" style={{ fontSize: '10px' }}>OS: {s.external_reference}</div>
                                                            </td>
                                                            <td className="fw-bold">{s.driver_name}</td>
                                                            <td className="small text-muted">{s.provider_name}</td>
                                                            <td className="small text-muted">
                                                                {s.country_code === 'CL' ? '🇨🇱 ' : s.country_code === 'DO' ? '🇩🇴 ' : ''}
                                                                {s.zone_name}
                                                            </td>
                                                            <td>
                                                                <Badge 
                                                                    bg={s.score >= 4.6 ? "success" : s.score >= 4.0 ? "warning" : "danger"} 
                                                                    text={s.score >= 4.0 && s.score < 4.6 ? "dark" : "white"}
                                                                    className="fw-bold px-2 py-1"
                                                                    style={{ fontSize: '11px' }}
                                                                >
                                                                    ⭐ {s.score.toFixed(1)}
                                                                </Badge>
                                                            </td>
                                                            <td className="small">
                                                                <div className="text-dark fw-normal">{s.comments || <em className="text-muted">Sin comentarios</em>}</div>
                                                                {s.internal_notes && (
                                                                    <div className="text-muted mt-1 x-small bg-light p-1 rounded border border-light" style={{ fontSize: '10px' }}>
                                                                        <strong>Gestión:</strong> {s.internal_notes}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Badge 
                                                                    bg={s.status === 'REVIEWED' ? "success" : "secondary"} 
                                                                    className="px-2 py-1 rounded-pill"
                                                                    style={{ fontSize: '9px' }}
                                                                >
                                                                    {s.status === 'REVIEWED' ? "🟢 GESTIONADO" : "🟠 PENDIENTE"}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-center">
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm" 
                                                                    className="py-1 px-2 fw-bold"
                                                                    style={{ fontSize: '10px' }}
                                                                    onClick={() => {
                                                                        setSelectedSurvey(s);
                                                                        setManagementNotes(s.internal_notes || '');
                                                                        setManagementStatus(s.status || 'REVIEWED');
                                                                        setShowManageModal(true);
                                                                    }}
                                                                >
                                                                    ⚙️ Gestionar
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={9} className="text-center p-4 text-muted">No hay encuestas recibidas para los filtros seleccionados.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    )}

                                    {qualityTab === 'incidents' && (
                                        <Table hover responsive className="mb-0 align-middle border-top">
                                            <thead className="bg-light">
                                                <tr className="small text-uppercase">
                                                    <th className="ps-4 py-3" style={{ width: '120px' }}>Fecha</th>
                                                    <th style={{ width: '150px' }}>Reserva / OS</th>
                                                    <th>Conductor</th>
                                                    <th>Proveedor</th>
                                                    <th>Zona</th>
                                                    <th>Tipo Incidencia</th>
                                                    <th>Responsable</th>
                                                    <th>Disputado</th>
                                                    <th>Estado</th>
                                                    <th style={{ width: '130px' }} className="text-center">Acción</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {displayedIncidents && displayedIncidents.length > 0 ? (
                                                    displayedIncidents.map((inc, idx) => (
                                                        <tr key={idx}>
                                                            <td className="ps-4 small text-muted">{formatDateStr(inc.scheduled_time.split('T')[0])}</td>
                                                            <td>
                                                                <div className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>#{inc.id}</div>
                                                                <div className="small text-muted font-monospace" style={{ fontSize: '10px' }}>OS: {inc.external_reference}</div>
                                                            </td>
                                                            <td className="fw-bold">{inc.driver_name}</td>
                                                            <td className="small text-muted">{inc.provider_name}</td>
                                                            <td className="small text-muted">
                                                                {inc.country_code === 'CL' ? '🇨🇱 ' : inc.country_code === 'DO' ? '🇩🇴 ' : ''}
                                                                {inc.zone_name}
                                                            </td>
                                                            <td className="small fw-bold text-danger">
                                                                ⚠️ {inc.incident_type}
                                                            </td>
                                                            <td>
                                                                <Badge bg={inc.incident_responsible === 'SUPPLIER' ? "danger" : "info"} className="px-2 py-1" style={{ fontSize: '9px' }}>
                                                                    {translateIncidentResponsible(inc.incident_responsible)}
                                                                </Badge>
                                                            </td>
                                                            <td>
                                                                <Form.Check 
                                                                    type="switch"
                                                                    id={`dispute-switch-${inc.id}`}
                                                                    label={inc.dispute_sent ? "Sí" : "No"}
                                                                    checked={inc.dispute_sent}
                                                                    disabled={updatingIncidentId === inc.id}
                                                                    style={{ fontSize: '11px' }}
                                                                    onChange={(e) => handleUpdateIncident(inc.id, inc.reviewed, e.target.checked)}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Badge 
                                                                    bg={
                                                                        inc.incident_status === 'RESOLVED_NO_PENALTY' || inc.incident_status === 'RESOLVED' ? "success" : 
                                                                        inc.incident_status === 'CONFIRMED_WITH_PENALTY' || inc.incident_status === 'PENALIZED' ? "danger" : "warning"
                                                                    } 
                                                                    className="px-2 py-1 rounded-pill"
                                                                    style={{ fontSize: '9px' }}
                                                                >
                                                                    {translateIncidentStatus(inc.incident_status)}
                                                                </Badge>
                                                            </td>
                                                            <td className="text-center">
                                                                <Button 
                                                                    variant={inc.reviewed ? "success" : "outline-warning"} 
                                                                    size="sm" 
                                                                    disabled={updatingIncidentId === inc.id}
                                                                    className="py-1 px-2 fw-bold d-flex align-items-center justify-content-center gap-1 mx-auto"
                                                                    style={{ fontSize: '10px' }}
                                                                    onClick={() => handleUpdateIncident(inc.id, !inc.reviewed)}
                                                                >
                                                                    {updatingIncidentId === inc.id ? (
                                                                        <Spinner animation="border" size="sm" style={{ width: '10px', height: '10px' }} />
                                                                    ) : inc.reviewed ? (
                                                                        "✓ Revisado"
                                                                    ) : (
                                                                        "Marcar Revisado"
                                                                    )}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={10} className="text-center p-4 text-muted">No hay incidentes reportados para los filtros seleccionados.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    )}
                                </Card.Body>
                            </Card>
                        </>
                    )}
                </>
            ) : activeTab === 'dispatchers' ? (
                <>
                    {/* FILTROS DE DISPERSIÓN LOCUTORAS */}
                    <Card className="mb-4 shadow-sm border-0">
                        <Card.Body className="p-3 bg-light rounded">
                            <Row className="align-items-end g-3">
                                <Col xl={2} lg={2} md={3} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Desde</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={dispersionFilters.start_date} 
                                        onChange={(e) => setDispersionFilters(prev => ({ ...prev, start_date: e.target.value }))} 
                                        size="sm" 
                                    />
                                </Col>
                                <Col xl={2} lg={2} md={3} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Hasta</Form.Label>
                                    <Form.Control 
                                        type="date" 
                                        value={dispersionFilters.end_date} 
                                        onChange={(e) => setDispersionFilters(prev => ({ ...prev, end_date: e.target.value }))} 
                                        size="sm" 
                                    />
                                </Col>
                                <Col xl={2} lg={2} md={3} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Locutora</Form.Label>
                                    <Form.Select 
                                        size="sm"
                                        value={dispersionFilters.dispatcher}
                                        onChange={(e) => setDispersionFilters(prev => ({ ...prev, dispatcher: e.target.value }))}
                                    >
                                        <option value="all">👥 Todas las Locutoras</option>
                                        {dispersionData?.available_dispatchers?.map(dName => (
                                            <option key={dName} value={dName}>🎧 {dName}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xl={2} lg={2} md={3} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase">Base / Ciudad</Form.Label>
                                    <Form.Select 
                                        size="sm"
                                        value={dispersionFilters.base_id}
                                        onChange={(e) => setDispersionFilters(prev => ({ ...prev, base_id: e.target.value }))}
                                    >
                                        <option value="">🏢 Todas las Bases</option>
                                        {bases.map(b => (
                                            <option key={b.id} value={b.id}>{b.name} ({b.country_code})</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col xl={1} lg={2} md={3} sm={6}>
                                    <Form.Label className="small fw-bold text-muted text-uppercase" title="Mínimo de viajes requeridos para calificar">Mín. Viajes</Form.Label>
                                    <Form.Select 
                                        size="sm"
                                        value={dispersionFilters.min_trips}
                                        onChange={(e) => setDispersionFilters(prev => ({ ...prev, min_trips: Number(e.target.value) }))}
                                    >
                                        <option value={10}>10 v.</option>
                                        <option value={15}>15 v.</option>
                                        <option value={20}>20 v.</option>
                                        <option value={25}>25 v.</option>
                                        <option value={30}>30 v.</option>
                                        <option value={50}>50 v.</option>
                                    </Form.Select>
                                </Col>
                                <Col xl={3} lg={2} md={9} sm={6} className="d-flex gap-2">
                                    <Button 
                                        variant="primary" 
                                        size="sm" 
                                        className="flex-fill fw-bold" 
                                        onClick={fetchDispatcherDispersion} 
                                        disabled={dispersionLoading}
                                    >
                                        {dispersionLoading ? <Spinner size="sm" animation="border" /> : <><Filter size={14} className="me-1" /> Filtrar KPI</>}
                                    </Button>
                                    <ButtonGroup size="sm">
                                        <Button 
                                            variant="outline-secondary" 
                                            title="Mes Actual"
                                            onClick={() => {
                                                const now = new Date();
                                                setDispersionFilters(prev => ({
                                                    ...prev,
                                                    start_date: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
                                                    end_date: now.toISOString().split('T')[0]
                                                }));
                                            }}
                                        >
                                            Mes
                                        </Button>
                                        <Button 
                                            variant="outline-secondary" 
                                            title="Mes Anterior"
                                            onClick={() => {
                                                const now = new Date();
                                                const prevMonthFirst = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                                                const prevMonthLast = new Date(now.getFullYear(), now.getMonth(), 0);
                                                setDispersionFilters(prev => ({
                                                    ...prev,
                                                    start_date: prevMonthFirst.toISOString().split('T')[0],
                                                    end_date: prevMonthLast.toISOString().split('T')[0]
                                                }));
                                            }}
                                        >
                                            Mes Ant.
                                        </Button>
                                    </ButtonGroup>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {error && <Alert variant="danger">{error}</Alert>}

                    {dispersionLoading && !dispersionData ? (
                        <div className="text-center p-5">
                            <Spinner animation="border" variant="primary" />
                            <h5 className="mt-3">Calculando dispersión de asignaciones de locutoras...</h5>
                        </div>
                    ) : dispersionData && (
                        <>
                            {/* KPI SUMMARY CARDS */}
                            <Row className="g-3 mb-4">
                                <Col xl={3} md={6}>
                                    <Card className="shadow-sm border-0 bg-white border-start border-4 border-primary">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="text-muted small fw-bold text-uppercase">Viajes Asignados (Final)</div>
                                                    <h3 className="fw-bold mb-0 text-primary mt-1">
                                                        {dispersionData.summary?.total_assignments?.toLocaleString('es-CL')}
                                                    </h3>
                                                    <small className="text-muted">Servicios activos del período</small>
                                                </div>
                                                <div className="p-3 bg-primary bg-opacity-10 rounded-circle text-primary">
                                                    <Radio size={24} />
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xl={3} md={6}>
                                    <Card className="shadow-sm border-0 bg-white border-start border-4 border-info">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="text-muted small fw-bold text-uppercase">Locutoras Evaluadas</div>
                                                    <h3 className="fw-bold mb-0 text-info mt-1">
                                                        {dispersionData.summary?.unique_dispatchers}
                                                    </h3>
                                                    <small className="text-muted">Mínimo {dispersionData.min_trips_threshold} viajes para calificar</small>
                                                </div>
                                                <div className="p-3 bg-info bg-opacity-10 rounded-circle text-info">
                                                    <Users size={24} />
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xl={3} md={6}>
                                    <Card className="shadow-sm border-0 bg-white border-start border-4 border-success">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="text-muted small fw-bold text-uppercase">Choferes Beneficiados</div>
                                                    <h3 className="fw-bold mb-0 text-success mt-1">
                                                        {dispersionData.summary?.unique_drivers}
                                                    </h3>
                                                    <small className="text-muted">Conductores de la flota asignados</small>
                                                </div>
                                                <div className="p-3 bg-success bg-opacity-10 rounded-circle text-success">
                                                    <Award size={24} />
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col xl={3} md={6}>
                                    <Card className="shadow-sm border-0 bg-white border-start border-4 border-warning">
                                        <Card.Body className="p-3">
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div>
                                                    <div className="text-muted small fw-bold text-uppercase">🏆 Líder Dispersión</div>
                                                    <h4 className="fw-bold mb-0 text-dark mt-1 text-truncate" style={{ maxWidth: '180px' }}>
                                                        {dispersionData.summary?.bonus_winner ? dispersionData.summary.bonus_winner.name : 'Sin clasificar'}
                                                    </h4>
                                                    <small className="text-muted">
                                                        {dispersionData.summary?.bonus_winner 
                                                            ? `Score: ${dispersionData.summary.bonus_winner.equity_score} pts (${dispersionData.summary.bonus_winner.total_assignments} viajes)` 
                                                            : 'Sin volumen suficiente'}
                                                    </small>
                                                </div>
                                                <div className="p-3 bg-warning bg-opacity-15 rounded-circle text-warning">
                                                    <Trophy size={24} />
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>

                            {/* PODIO DE DISPERSIÓN */}
                            {dispersionData.podium && dispersionData.podium.length > 0 && (
                                <Card className="shadow-sm border-0 mb-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: 'white' }}>
                                    <Card.Body className="p-4">
                                        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="p-2 bg-warning bg-opacity-25 rounded-circle text-warning d-flex align-items-center justify-content-center">
                                                    <Trophy size={22} />
                                                </span>
                                                <div>
                                                    <h5 className="fw-bold mb-0 text-white d-flex align-items-center gap-2">
                                                        Podio de Equidad y Dispersión
                                                    </h5>
                                                    <small className="text-light opacity-75">
                                                        Premia menor concentración y mayor variedad de choferes asignados (Mínimo {dispersionData.min_trips_threshold} viajes)
                                                    </small>
                                                </div>
                                            </div>
                                            {dispersionData.podium[0] && (
                                                <Badge bg="warning" text="dark" className="px-3 py-2 fw-bold fs-6 shadow-sm">
                                                    🥇 1° Lugar: {dispersionData.podium[0].name} ({dispersionData.podium[0].equity_score} pts)
                                                </Badge>
                                            )}
                                        </div>

                                        <Row className="g-3">
                                            {dispersionData.podium.map((pod, pIdx) => {
                                                const isFirst = pIdx === 0;
                                                const isSecond = pIdx === 1;
                                                const accentColor = isFirst ? '#ffc107' : isSecond ? '#e2e8f0' : '#f59e0b';
                                                const medal = pod.medal;
                                                const isSelected = selectedDispatcherDetail?.name === pod.name;

                                                return (
                                                    <Col key={pod.name} lg={4} md={12}>
                                                        <Card 
                                                            className={`h-100 shadow-sm border-0`}
                                                            style={{ 
                                                                cursor: 'pointer',
                                                                background: isFirst ? 'rgba(255, 193, 7, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                                                                border: isSelected ? '2px solid #38bdf8' : isFirst ? '2px solid rgba(255, 193, 7, 0.6)' : '1px solid rgba(255, 255, 255, 0.15)',
                                                                borderRadius: '12px',
                                                                transition: 'all 0.2s ease-in-out'
                                                            }}
                                                            onClick={() => setSelectedDispatcherDetail(pod)}
                                                        >
                                                            <Card.Body className="p-3 d-flex flex-column justify-content-between">
                                                                <div>
                                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <span className="fs-2">{medal}</span>
                                                                            <div>
                                                                                <div className="text-uppercase small fw-bold" style={{ color: accentColor, fontSize: '11px', letterSpacing: '0.5px' }}>
                                                                                    {pod.award_title}
                                                                                </div>
                                                                                <h5 className="fw-bold mb-0 text-white">{pod.name}</h5>
                                                                            </div>
                                                                        </div>
                                                                        <div className="text-end">
                                                                            <span className="badge rounded-pill" style={{ backgroundColor: accentColor, color: '#0f172a', fontWeight: 'bold', fontSize: '13px' }}>
                                                                                {pod.equity_score} pts
                                                                            </span>
                                                                            <div className="small text-light opacity-75 mt-1" style={{ fontSize: '10px' }}>Score Equidad</div>
                                                                        </div>
                                                                    </div>

                                                                    <div className="row g-2 mt-2 pt-2 border-top border-secondary border-opacity-50 text-center">
                                                                        <div className="col-4">
                                                                            <div className="small opacity-75" style={{ fontSize: '10px' }}>Viajes</div>
                                                                            <strong className="text-white fs-6">{pod.total_assignments}</strong>
                                                                        </div>
                                                                        <div className="col-4">
                                                                            <div className="small opacity-75" style={{ fontSize: '10px' }}>Choferes</div>
                                                                            <strong className="text-white fs-6">{pod.unique_drivers}</strong>
                                                                        </div>
                                                                        <div className="col-4">
                                                                            <div className="small opacity-75" style={{ fontSize: '10px' }}>Top 1 %</div>
                                                                            <strong style={{ color: pod.top1_pct <= 25 ? '#4ade80' : pod.top1_pct <= 35 ? '#facc15' : '#f87171' }}>
                                                                                {pod.top1_pct}%
                                                                            </strong>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="mt-3 pt-2 border-top border-secondary border-opacity-25 small opacity-90 text-light" style={{ fontSize: '11px', lineHeight: 1.35 }}>
                                                                    {pod.bonus_rationale}
                                                                </div>
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* CONTENIDO PRINCIPAL: RANKING COMPARATIVO Y DETALLE */}
                            <Row className="g-4">
                                {/* COLUMNA IZQUIERDA: COMPARATIVA GENERAL DE LOCUTORAS */}
                                <Col lg={7}>
                                    <Card className="shadow-sm border-0 mb-4">
                                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                                            <div>
                                                <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2">
                                                    <Radio size={18} /> Tabla Clasificatoria de Locutoras
                                                </h6>
                                                <small className="text-muted">Ordenada por Score de Equidad y efectividad de asignación.</small>
                                            </div>
                                            <Badge bg="secondary">{dispersionData.dispatchers?.length || 0} Locutoras</Badge>
                                        </Card.Header>
                                        <Card.Body className="p-0">
                                            <div className="table-responsive">
                                                <Table hover className="align-middle mb-0 text-nowrap">
                                                    <thead className="table-light text-muted small text-uppercase">
                                                        <tr>
                                                            <th className="text-center">#</th>
                                                            <th>Locutora</th>
                                                            <th className="text-center">Score Equidad</th>
                                                            <th className="text-center">Viajes</th>
                                                            <th className="text-center">Choferes</th>
                                                            <th>Conductor Favorito (Top 1)</th>
                                                            <th className="text-center">Top 3 (%)</th>
                                                            <th className="text-center">Estado</th>
                                                            <th className="text-end">Acción</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {dispersionData.dispatchers?.map((disp, idx) => {
                                                            const isSelected = selectedDispatcherDetail?.name === disp.name;
                                                            return (
                                                                <tr 
                                                                    key={disp.name} 
                                                                    onClick={() => setSelectedDispatcherDetail(disp)}
                                                                    style={{ 
                                                                        cursor: 'pointer',
                                                                        backgroundColor: isSelected ? 'rgba(13, 110, 253, 0.08)' : undefined
                                                                    }}
                                                                >
                                                                    <td className="text-center fw-bold">
                                                                        {disp.medal && disp.medal !== '-' ? (
                                                                            <span className="fs-5">{disp.medal}</span>
                                                                        ) : (
                                                                            <span className="text-muted small">-</span>
                                                                        )}
                                                                    </td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <div 
                                                                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold"
                                                                                style={{ 
                                                                                    width: '32px', 
                                                                                    height: '32px', 
                                                                                    backgroundColor: disp.rank === 1 ? '#ffc107' : disp.rank === 2 ? '#6c757d' : disp.rank === 3 ? '#d97706' : '#0d6efd',
                                                                                    color: disp.rank === 1 ? '#000' : '#fff',
                                                                                    fontSize: '12px'
                                                                                }}
                                                                            >
                                                                                {disp.name.slice(0, 2).toUpperCase()}
                                                                            </div>
                                                                            <div>
                                                                                <div className="fw-bold">{disp.name}</div>
                                                                                <small className="text-muted" style={{ fontSize: '11px' }}>Venta: ${disp.total_sales?.toLocaleString('es-CL')}</small>
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Badge 
                                                                            bg={disp.equity_score >= 70 ? 'success' : disp.equity_score >= 45 ? 'warning' : 'danger'}
                                                                            className="px-2 py-1 fw-bold fs-6"
                                                                        >
                                                                            {disp.equity_score} pts
                                                                        </Badge>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className="fw-bold fs-6">{disp.total_assignments}</span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Badge bg="light" text="dark" className="border">
                                                                            {disp.unique_drivers} choferes
                                                                        </Badge>
                                                                    </td>
                                                                    <td>
                                                                        <div className="small fw-semibold text-truncate" style={{ maxWidth: '140px' }} title={disp.top1_driver}>
                                                                            {disp.top1_driver}
                                                                        </div>
                                                                        <div className="d-flex align-items-center gap-2 mt-1">
                                                                            <ProgressBar 
                                                                                now={disp.top1_pct} 
                                                                                variant={disp.top1_pct > 35 ? 'danger' : disp.top1_pct > 25 ? 'warning' : 'success'} 
                                                                                style={{ height: '6px', width: '65px' }} 
                                                                            />
                                                                            <small className="text-muted fw-bold" style={{ fontSize: '11px' }}>{disp.top1_pct}%</small>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <span className={`fw-bold ${disp.top3_pct > 60 ? 'text-danger' : disp.top3_pct > 45 ? 'text-warning' : 'text-success'}`}>
                                                                            {disp.top3_pct}%
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-center">
                                                                        {disp.qualifies_for_bonus ? (
                                                                            <Badge bg="success" className="bg-opacity-25 text-success border border-success">
                                                                                ✓ Califica
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge bg="secondary" className="bg-opacity-25 text-dark border border-secondary" title={`Requiere al menos ${dispersionData.min_trips_threshold} viajes`}>
                                                                                Vol. Bajo
                                                                            </Badge>
                                                                        )}
                                                                    </td>
                                                                    <td className="text-end">
                                                                        <Button 
                                                                            variant={isSelected ? 'primary' : 'outline-primary'} 
                                                                            size="sm"
                                                                            className="px-2 py-1"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                setSelectedDispatcherDetail(disp);
                                                                            }}
                                                                        >
                                                                            {isSelected ? 'Viendo 👁️' : 'Detalle'}
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>

                                {/* COLUMNA DERECHA: DESGLOSE DE CHOFERES DE LA LOCUTORA SELECCIONADA */}
                                <Col lg={5}>
                                    {selectedDispatcherDetail ? (
                                        <Card className="shadow-sm border-0 sticky-top" style={{ top: '20px' }}>
                                            <Card.Header className="bg-white py-3 border-bottom">
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <span className="small text-muted text-uppercase fw-bold">Desglose de Reparto</span>
                                                            {selectedDispatcherDetail.medal && selectedDispatcherDetail.medal !== '-' && (
                                                                <Badge bg="warning" text="dark" className="fw-bold">{selectedDispatcherDetail.medal} {selectedDispatcherDetail.award_title}</Badge>
                                                            )}
                                                        </div>
                                                        <h5 className="fw-bold mb-0 text-dark d-flex align-items-center gap-2 mt-1">
                                                            🎧 {selectedDispatcherDetail.name}
                                                        </h5>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="fs-5 fw-bold text-primary">{selectedDispatcherDetail.total_assignments} viajes</div>
                                                        <small className="text-muted">{selectedDispatcherDetail.unique_drivers} choferes distintos</small>
                                                    </div>
                                                </div>
                                                
                                                <div className="row g-2 mt-2 pt-2 border-top">
                                                    <div className="col-4">
                                                        <div className="p-2 bg-light rounded text-center">
                                                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>Score Equidad</small>
                                                            <strong className="text-dark">{selectedDispatcherDetail.equity_score} pts</strong>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="p-2 bg-light rounded text-center">
                                                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>Venta Asignada</small>
                                                            <strong className="text-success">${selectedDispatcherDetail.total_sales?.toLocaleString('es-CL')}</strong>
                                                        </div>
                                                    </div>
                                                    <div className="col-4">
                                                        <div className="p-2 bg-light rounded text-center">
                                                            <small className="text-muted d-block" style={{ fontSize: '10px' }}>Costo Chofer</small>
                                                            <strong className="text-primary">${selectedDispatcherDetail.total_cost?.toLocaleString('es-CL')}</strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Card.Header>

                                            <Card.Body className="p-0">
                                                <div style={{ maxHeight: '520px', overflowY: 'auto' }}>
                                                    <Table hover className="align-middle mb-0" size="sm">
                                                        <thead className="table-light small text-muted text-uppercase sticky-top" style={{ top: 0 }}>
                                                            <tr>
                                                                <th>Conductor / Móvil</th>
                                                                <th className="text-center">Viajes</th>
                                                                <th style={{ width: '40%' }}>% Reparto</th>
                                                                <th className="text-end">Venta</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {selectedDispatcherDetail.drivers_breakdown?.map((item, dIdx) => (
                                                                <tr key={item.driver_name}>
                                                                    <td>
                                                                        <div className="fw-bold small">{item.driver_name}</div>
                                                                        <small className="text-muted" style={{ fontSize: '11px' }}>
                                                                            {item.vehicle_code && item.vehicle_code !== 'S/I' ? `Móvil: ${item.vehicle_code}` : 'Móvil S/I'}
                                                                        </small>
                                                                    </td>
                                                                    <td className="text-center fw-bold">{item.count}</td>
                                                                    <td>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <ProgressBar 
                                                                                now={item.percentage} 
                                                                                variant={dIdx === 0 && item.percentage > 35 ? 'danger' : item.percentage > 20 ? 'warning' : 'primary'}
                                                                                style={{ height: '7px', flex: 1 }} 
                                                                            />
                                                                            <small className="fw-bold" style={{ minWidth: '35px', fontSize: '11px' }}>
                                                                                {item.percentage}%
                                                                            </small>
                                                                        </div>
                                                                    </td>
                                                                    <td className="text-end small fw-semibold text-muted">
                                                                        ${Number(item.sales || 0).toLocaleString('es-CL')}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ) : (
                                        <Card className="shadow-sm border-0 text-center p-5 bg-light">
                                            <div className="text-muted">Selecciona una locutora para ver su detalle de asignaciones.</div>
                                        </Card>
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}
                </>
            ) : null}

            {/* MODAL DE GESTIÓN DE CALIDAD */}
            <Modal show={showManageModal} onHide={() => setShowManageModal(false)} centered>
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="fw-bold" style={{ fontSize: '1.15rem' }}>
                        ⚙️ Gestión de Calidad - OS {selectedSurvey?.external_reference}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedSurvey && (
                        <div className="d-flex flex-column gap-3">
                            <div className="bg-light p-3 rounded border">
                                <div className="small text-muted mb-2"><strong>Detalle del Servicio:</strong></div>
                                <div className="small"><strong>Conductor:</strong> {selectedSurvey.driver_name}</div>
                                <div className="small"><strong>Proveedor:</strong> {selectedSurvey.provider_name}</div>
                                <div className="small"><strong>Zona:</strong> {selectedSurvey.zone_name}</div>
                                <div className="small mt-2">
                                    <strong>Calificación Cliente: </strong> 
                                    <Badge bg={selectedSurvey.score >= 4.6 ? "success" : selectedSurvey.score >= 4.0 ? "warning" : "danger"} className="fw-bold">
                                        ⭐ {selectedSurvey.score.toFixed(1)}
                                    </Badge>
                                </div>
                                <div className="small mt-1 text-muted">
                                    <strong>Comentario del Pax:</strong> "{selectedSurvey.comments || 'Sin comentarios'}"
                                </div>
                            </div>
                            
                            <Form.Group>
                                <Form.Label className="small fw-bold text-muted text-uppercase">Bitácora de Gestión Interna</Form.Label>
                                <Form.Control 
                                    as="textarea" 
                                    rows={3} 
                                    placeholder="Describe qué medidas se tomaron con el conductor o proveedor..." 
                                    value={managementNotes}
                                    onChange={(e) => setManagementNotes(e.target.value)}
                                    style={{ fontSize: '0.9rem' }}
                                />
                                <Form.Text className="text-muted x-small">
                                    Esta nota quedará guardada de forma privada en el historial de calidad del viaje.
                                </Form.Text>
                            </Form.Group>
                            
                            <Form.Group>
                                <Form.Label className="small fw-bold text-muted text-uppercase">Cambiar Estado</Form.Label>
                                <Form.Select 
                                    value={managementStatus} 
                                    onChange={(e) => setManagementStatus(e.target.value)}
                                    size="sm"
                                    className="fw-bold text-uppercase"
                                >
                                    <option value="PENDING">🟠 Mantener Pendiente</option>
                                    <option value="REVIEWED">🟢 Marcar como Gestionado</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" className="fw-bold" size="sm" onClick={() => setShowManageModal(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        variant="success" 
                        className="fw-bold" 
                        size="sm" 
                        onClick={async () => {
                            if (!selectedSurvey) return;
                            try {
                                await updateSurveyManagement(selectedSurvey.id, {
                                    status: managementStatus,
                                    internal_notes: managementNotes
                                });
                                setShowManageModal(false);
                                fetchBookingQuality();
                            } catch (err) {
                                console.error("Error al guardar gestión:", err);
                                alert("Ocurrió un error al guardar los cambios.");
                            }
                        }}
                    >
                        Guardar Cambios
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default InsightsDashboard;
